<template>
  <!-- ประเมินความพึงพอใจ (TH)/(EN), moved here from the CustomerInformationV2026 toolbar. It only
       drops the survey link into the composer (SET_COMPOSER_INSERT); the admin still presses ส่งข้อความ. -->
  <div v-if="options.length" class="v2026-fb-anchor" @keydown.esc="onEsc">
    <!-- .v2026-emoji = the same closed box as the sticker / คีย์ลัด triggers next to it. -->
    <button type="button"
            ref="triggerBtn"
            class="v2026-emoji v2026-fb-trigger"
            :class="{ 'is-open': open, 'is-busy': isBusy }"
            aria-haspopup="menu"
            aria-label="ประเมินความพึงพอใจ"
            title="ประเมินความพึงพอใจ"
            :aria-expanded="open ? 'true' : 'false'"
            :aria-busy="isBusy ? 'true' : null"
            :disabled="isBusy"
            @click.prevent="toggle">
      <img :src="rateIcon" class="v2026-fb-ico" alt="">
      <i class="fas v2026-chev" :class="open ? 'fa-chevron-down' : 'fa-chevron-up'" aria-hidden="true"></i>
    </button>

    <OverlayCard class="v2026-fb-pos"
                 :open="open"
                 v-show="open"
                 :ignore="triggerEl"
                 :renderKey="renderKey"
                 role="menu"
                 aria-label="ประเมินความพึงพอใจ"
                 @close="hide()">
      <template #default="rk">
        <button v-for="opt in options"
                :key="opt.lang"
                type="button"
                class="v2026-fb-item"
                role="menuitem"
                :data-rk="rk.dirty"
                @click="pick(opt.lang)">
          <img :src="rateIcon" class="v2026-fb-ico" alt="">
          <span class="v2026-fb-item__label">ประเมินความพึงพอใจ</span>
          <span class="v2026-fb-item__lang">({{ opt.label }})</span>
        </button>
      </template>
    </OverlayCard>

    <BaseToast :open="toast.open" :variant="toast.variant" :title="toast.title" :message="toast.message"
               :autoHideDelay="toast.autoHideDelay" :icon="toast.icon"
               @close="toast.open = false" />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import axios from 'axios'
import OverlayCard from '@/components/coversation/OverlayCard'
import BaseToast from '@/components/coversation/BaseToast.vue'

// Shared by the success toast and the composer's hint strip so both read identically.
const FEEDBACK_APPEND_HINT = 'ข้อความที่พิมพ์ไว้และข้อความประเมินจะถูกส่งรวมเป็นข้อความเดียว'

// Errors dwell longest: a failure the admin misses reads as "the button does nothing".
const FEEDBACK_TOAST_MS = { inserted: 2500, appended: 5000, failed: 6000 }

export default {
  name: 'feedback-menu-v2026',
  components: {
    OverlayCard,
    BaseToast
  },
  props: {
    selectedConversation: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      open: false,
      triggerEl: null,
      rateIcon: require('@/assets/icons2026/composer-rate.svg'),
      // Fresh token on every commit so the composer's watcher fires even when the text repeats.
      insertToken: 0,
      // null | 'th' | 'en' — the request in flight. Always cleared in a finally.
      busyLang: null,
      toast: { open: false, variant: 'success', title: '', message: '', autoHideDelay: FEEDBACK_TOAST_MS.inserted, icon: 'check' }
    }
  },
  computed: {
    ...mapState('admin', ['admin']),
    conversationId () {
      return this.selectedConversation ? this.selectedConversation.id : null
    },
    // Master's guards verbatim: TH follows checkCustomerType (customer, beneat_professional, and
    // professional except on a PrivateConversation), EN is the plain customer rail only.
    options () {
      const conv = this.selectedConversation
      if (!conv) return []
      const list = []
      if (this.checkCustomerType(conv.customer_type, conv.type)) list.push({ lang: 'th', label: 'TH' })
      if (conv.customer_type === 'customer') list.push({ lang: 'en', label: 'EN' })
      return list
    },
    isBusy () {
      return this.busyLang !== null
    },
    renderKey () {
      return (this.open ? 1 : 0) + '#' + this.options.map(o => o.lang).join(',')
    }
  },
  watch: {
    conversationId () {
      this.hide()
    }
  },
  mounted () {
    this.triggerEl = this.$refs.triggerBtn || null
  },
  methods: {
    toggle () {
      if (this.open) this.hide(true)
      else this.open = true
    },
    hide (returnFocus = false) {
      this.open = false
      if (returnFocus) {
        this.$nextTick(() => {
          if (this.$refs.triggerBtn) this.$refs.triggerBtn.focus()
        })
      }
    },
    onEsc (e) {
      if (!this.open) return
      e.stopPropagation()
      this.hide(true)
    },
    pick (lang) {
      this.hide()
      this.createFeedbackUrl(lang, this.selectedConversation.customer_type)
    },
    checkCustomerType (customerType, type) {
      if (customerType === 'customer' || customerType === 'beneat_professional') {
        return true
      }
      return customerType === 'professional' && type !== 'PrivateConversation'
    },
    showToast (variant, title, message, autoHideDelay = FEEDBACK_TOAST_MS.inserted, icon = 'check') {
      this.toast = { open: true, variant, title, message, autoHideDelay, icon }
    },
    showFeedbackError (message) {
      this.showToast('danger', 'สร้างลิงก์ประเมินไม่สำเร็จ', message || 'กรุณาลองใหม่อีกครั้ง', FEEDBACK_TOAST_MS.failed, 'alert')
    },
    async createFeedbackUrl (lang, type) {
      // Two clicks would mint two feedback rows.
      if (this.isBusy) return
      let url = '/conversations/feedback-url'
      const params = {
        admin_id: this.admin.id,
        conversation_id: this.selectedConversation.id
      }
      if (type === 'beneat_professional') {
        url = '/conversations/feedback-professional-url'
      } else if (type === 'professional') {
        url = '/conversations/feedback-professional-register-url'
      }
      // Captured BEFORE the await: the composer's draft survives a conversation switch, so without
      // this the response could park customer A's link in a draft aimed at customer B.
      const convId = this.selectedConversation.id
      this.busyLang = lang
      try {
        await this.requestFeedbackUrl(url, params, lang, type, convId)
      } catch (e) {
        const serverMessage = e && e.response && e.response.data && e.response.data.message
        this.showFeedbackError(serverMessage)
      } finally {
        // Not optional: without it one failed request leaves the button dead for the session.
        this.busyLang = null
      }
    },
    async requestFeedbackUrl (url, params, lang, type, convId) {
      const { data } = await axios.get(url, { params })
      if (data.error || !data.feedback) {
        this.showFeedbackError(data.message)
        return
      }
      // Never insert a survey URL ending in id=undefined.
      if (!data.feedback.id) {
        this.showFeedbackError(null)
        return
      }
      let text = `[ข้อความอัตโนมัติ] BeNeat Customer Service สวัสดีค่ะ ขอบพระคุณคุณลูกค้าที่ให้โอกาสเจ้าหน้าที่ได้อำนวยความสะดวกคุณลูกค้าครั้งนี้ กรุณาให้คะแนนประเมินการติดต่อประสานงานของเจ้าหน้าที่ในครั้งนี้ https://beneat.co/feedback/operation?id=${data.feedback.id}`
      if (type === 'beneat_professional') {
        text = `[ข้อความอัตโนมัติ] BeNeat Customer Service สวัสดีค่ะ เราขอขอบคุณ คุณแม่บ้านที่ให้โอกาสเจ้าหน้าที่ได้อำนวยความสะดวกในครั้งนี้ กรุณาให้คะแนนประเมินการติดต่อประสานงานของเจ้าหน้าที่ในครั้งนี้ https://beneat.co/feedback/operation-professional?id=${data.feedback.id}`
      } else if (type === 'professional') {
        text = `[ข้อความอัตโนมัติ] ยินดีด้วยค่ะ คุณแม่บ้านผ่านหลักสูตรการอบรม คุณแม่บ้าน BeNeat แบบมืออาชีพ กรุณาประเมินความพึงพอใจในการอบรมครั้งนี้ ขอบพระคุณค่ะ https://beneat.co/feedback/operation-professional-register?id=${data.feedback.id}`
      }
      if (lang === 'en') {
        text = `[Automatic Message] Thank you for giving our staff the opportunity to assist you on this occasion. Please rate your satisfaction with how our staff coordinated and communicated with you during this interaction.  https://beneat.co/feedback/operation?id=${data.feedback.id}&lang=en`
        if (type === 'beneat_professional') {
          text = `[Automatic Message] Hello,  we would like to say thank you for giving our staff the opportunity assisting you this time. Please provide a rating for the coordination of our staff on this occasion.  https://beneat.co/feedback/operation-professional?id=${data.feedback.id}&lang=en`
        }
      }
      if (!this.selectedConversation || this.selectedConversation.id !== convId) {
        this.showToast('danger', 'ยกเลิกข้อความประเมิน', 'คุณเปลี่ยนแชทระหว่างสร้างลิงก์ กรุณากดใหม่ในแชทที่ต้องการ', FEEDBACK_TOAST_MS.failed, 'alert')
        return
      }
      // Same pipe the translate assistant uses; the composer resolves append-vs-replace. It never sends.
      const payload = { text, token: ++this.insertToken, hint: FEEDBACK_APPEND_HINT }
      this.$store.commit('aiAssistant/SET_COMPOSER_INSERT', payload)
      // The composer's watcher runs before this callback, so `appended` reports what happened.
      this.$nextTick(() => {
        if (payload.appended) {
          this.showToast('success', 'เพิ่มข้อความประเมินต่อท้ายข้อความเดิมแล้ว', FEEDBACK_APPEND_HINT, FEEDBACK_TOAST_MS.appended)
        } else {
          this.showToast('success', 'ใส่ข้อความประเมินในช่องพิมพ์แล้ว', 'ตรวจข้อความแล้วกด "ส่งข้อความ"', FEEDBACK_TOAST_MS.inserted)
        }
      })
    }
  }
}
</script>

<style scoped>
.v2026-fb-anchor {
  position: relative;
  display: inline-flex;
  vertical-align: middle;
  flex-shrink: 0;
}

.v2026-fb-trigger {
  border-radius: 0.25rem;
  font-family: inherit;
  cursor: pointer;
  -webkit-appearance: none;
     -moz-appearance: none;
          appearance: none;
}

.v2026-fb-trigger:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}

.v2026-fb-trigger.is-open .v2026-chev {
  color: var(--v2026-color-main, #31C1D7);
}

/* In flight: dimmed, not clickable, a sliding bar along the bottom edge. */
.v2026-fb-trigger.is-busy {
  position: relative;
  overflow: hidden;
  opacity: 0.65;
  cursor: progress;
}

.v2026-fb-trigger.is-busy::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 40%;
  height: 2px;
  background: var(--v2026-color-main, #31C1D7);
  animation: v2026-fb-progress 900ms linear infinite;
}

@keyframes v2026-fb-progress {
  from { transform: translateX(-100%); }
  to   { transform: translateX(350%); }
}

.v2026-chev {
  font-size: 10px;
  color: var(--v2026-text-muted, #98A2B3);
}

.v2026-fb-ico {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: block;
}

/* Opens upward, right edge on the trigger's right edge. */
.v2026-fb-anchor .v2026-fb-pos {
  bottom: calc(100% + 8px);
  right: 0;
  left: auto;
  min-width: 0;
  width: max-content;
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.v2026-fb-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 0;
  border-radius: var(--v2026-r-btn, 10px);
  background: transparent;
  font-family: inherit;
  font-size: 15px;
  line-height: 1.2;
  color: var(--v2026-text-primary, #1D2939);
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 150ms ease;
}

.v2026-fb-item:hover {
  background: var(--v2026-surface-hover, #E9ECF1);
}

.v2026-fb-item:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 2px rgba(49, 193, 215, 0.55);
}

.v2026-fb-item__lang {
  color: var(--v2026-color-main, #31C1D7);
}

@media (prefers-reduced-motion: reduce) {
  .v2026-fb-item {
    transition: none;
  }

  .v2026-fb-trigger.is-busy::after {
    width: 100%;
    animation: none;
  }
}
</style>
